import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Button, Toggle, Loading } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findModifierSettings, updateModifierSettings } from '../actions';
import { fetchIsModifierCompany } from '../../../reports/billHistory/utils/companyType';

const PARAM = 'is-product-modifiers-enabled';

const ModifierSettings = memo(
  ({
    modifierSettingsState: { modifierSettingsData },
    auth: { companyId },
    showAlert,
    findModifierSettings,
    updateModifierSettings,
  }) => {
    const originalDataRef = useRef({});
    const [form, setForm] = useState({ [PARAM]: 'false' });
    const [isLoading, setIsLoading] = useState(false);
    const [modifiersEnabled, setModifiersEnabled] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        const enabled = await fetchIsModifierCompany(companyId);
        if (cancelled) return;
        setModifiersEnabled(enabled);
        if (enabled) {
          findModifierSettings();
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [companyId, findModifierSettings]);

    useEffect(() => {
      const nextForm = { [PARAM]: 'false' };
      if (Array.isArray(modifierSettingsData)) {
        for (const item of modifierSettingsData) {
          if (item?.param === PARAM) {
            nextForm[PARAM] = item.value ?? 'false';
          }
        }
      }
      originalDataRef.current = { ...nextForm };
      setForm(nextForm);
    }, [modifierSettingsData]);

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(form).forEach((key) => {
        if ((form[key] ?? '') !== (originalDataRef.current[key] ?? '')) {
          changedFields[key] = form[key];
        }
      });
      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }
      const result = await updateModifierSettings({
        modifierSettingsData: changedFields,
      });
      showAlert({
        isAlertOpen: true,
        alertType: result ? 'success' : 'danger',
        alertMessage: result
          ? 'Modifier settings updated successfully!'
          : 'Failed to update modifier settings!',
      });
      setIsLoading(false);
      findModifierSettings();
    };

    if (modifiersEnabled == null) {
      return (
        <div className={styles.container}>
          <Loading variant="overlay" label="" />
        </div>
      );
    }

    if (modifiersEnabled === false) {
      return (
        <div className={styles.container}>
          <header className={styles.pageHeader}>
            <div className={styles.titleSection}>
              <h1 className={styles.pageTitle}>Modifier Settings</h1>
              <p className={styles.breadcrumb}>Settings / Modifiers</p>
            </div>
          </header>
          <div className={styles.body}>
            <p className={styles.emptyState}>
              Product modifiers are only available for restaurant and retail companies.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Modifier Settings</h1>
            <p className={styles.breadcrumb}>Settings / Modifiers</p>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topicDiv}>
                <h2 className={styles.topicLabel}>Product modifiers</h2>
                <p className={styles.topicCaption}>
                  Turn on priced extras on the POS. Catalog CRUD stays available either way.
                </p>
              </div>
              <div className={styles.rowDiv}>
                <Toggle
                  checked={form[PARAM] === 'true'}
                  onChange={handleToggleChange}
                  name={PARAM}
                  label="Enable product modifiers"
                  labelPlacement="start"
                />
              </div>
              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => navigate('/')}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  modifierSettingsState: state.modifierSettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  updateModifierSettings,
  findModifierSettings,
})(ModifierSettings);
